import React from "react";
import { useTranslation } from "react-i18next";
import { Platform, View } from "react-native";
import {
  FlexGrid,
  Text,
  CTA,
  theme,
  Heading,
  formatCurrency,
  useResponsive,
} from "@clearabee/ui-library-native";
import { Input } from "components/Core/Input/Input";
import { Image } from "components/Image/Image";
import placeholderImage from "lib/images/placeholder.png";
import { FlatCatalogue } from "@clearabee/ui-sdk";
import { createResponsiveStyles } from "@clearabee/ui-library/src/utils/createResponsiveStyles";
import { useFormContext } from "lib/hooks/useFormContext";

interface ProductListProps {
  products: FlatCatalogue["items"];
}

const Wrapper = ({ children }: React.PropsWithChildren): React.ReactElement => {
  if (Platform.OS === "web")
    return (
      <div
        css={createResponsiveStyles({
          base: {
            overflowY: "scroll",
            overflowX: "hidden",
            maxHeight: 700,
          },
          medium: {
            "::-webkit-scrollbar": {
              width: theme.spacing.small,
              position: "absolute",
              right: -theme.spacing.small,
            },
            "::-webkit-scrollbar-track": {
              background: theme.colors.greyscale.dark,
              borderRadius: theme.spacing.small,
            },
            "::-webkit-scrollbar-thumb": {
              background: theme.colors.warning.base,
              borderRadius: theme.spacing.small,
            },
            "::-webkit-scrollbar-thumb:hover": {
              background: theme.colors.warning.dark,
            },
          },
        })}
      >
        {children}
      </div>
    );
  return <View style={{ overflow: "hidden" }}>{children}</View>;
};

export const ProductList = ({
  products,
}: ProductListProps): React.ReactElement => {
  const [translate] = useTranslation();
  const { screens } = useResponsive();
  const { Field } = useFormContext();

  return (
    <View
      style={{
        paddingVertical: theme.spacing.medium,
        position: "relative",
      }}
    >
      <Wrapper>
        <FlexGrid
          cellSpacing="xsmall"
          rowSpacing="xsmall"
          style={
            Platform.OS === "web" &&
            screens.medium && { marginRight: theme.spacing.xsmall }
          }
        >
          {products
            ?.sort((a, b) => a.title.localeCompare(b.title))
            .map((product) => (
              <FlexGrid.Cell
                key={product.sku}
                base="50%"
                medium="33.33%"
                large="25%"
              >
                <CTA
                  styles={{ flex: 1 }}
                  shadow={Platform.OS === "web"}
                  heading={
                    <Heading
                      color="brand"
                      level={4}
                      styles={{
                        marginHorizontal: "auto",
                        textAlign: "center",
                      }}
                    >
                      {product.title}
                    </Heading>
                  }
                  subheading={
                    <View
                      style={{
                        marginHorizontal: "auto",
                        marginTop: "auto",
                      }}
                    >
                      {
                        <Text
                          numberOfLines={1}
                          styles={{
                            color: theme.colors.dark.base,
                            fontFamily: "ProximaBold",
                          }}
                        >
                          {formatCurrency(product.price ?? 0)}
                          <Text
                            styles={{
                              fontWeight: "100",
                            }}
                          >
                            {" "}
                            {translate("common.incVat")}
                          </Text>
                        </Text>
                      }
                    </View>
                  }
                  orientation="portrait"
                  textColor="brand"
                  background="light"
                  image={
                    <Image
                      source={product.img ?? placeholderImage}
                      resizeMode="contain"
                      style={{
                        width: "90%",
                        height: "90%",
                      }}
                    />
                  }
                >
                  <View
                    style={{
                      marginHorizontal: "auto",
                      marginTop: "auto",
                      marginVertical: theme.spacing.xsmall,
                    }}
                  >
                    <Field name={`products.${product.sku}`}>
                      {({ field }) => <Input.Quantity {...field} />}
                    </Field>
                  </View>
                </CTA>
              </FlexGrid.Cell>
            ))}
        </FlexGrid>
      </Wrapper>
    </View>
  );
};
